// The items of a row-action or split-button menu, and the minimum width of its surface. An item is a command
// (onSelect) or a destination (href); either may carry a leading icon and a trailing hint.

import React from 'react'

import { Button } from '@CommonComponents/buttons'
import { DEFAULT_MENU_MIN_WIDTH } from '../config/defaults'

const FAMILY = /(^|\s)fa-(regular|solid|light|thin|duotone|brands)(\s|$)/

// A Font Awesome name ('fa-receipt' or 'receipt') takes the regular family unless it names its own.
export const iconClass = (icon, extra) => {
  if (!icon || typeof icon !== 'string') return undefined
  const name = icon.startsWith('fa-') || FAMILY.test(icon) ? icon : `fa-${icon}`
  return [FAMILY.test(name) ? '' : 'fa-regular', name, extra]
    .filter(Boolean)
    .join(' ')
}

const isText = value => typeof value === 'string' && value.trim() !== ''

// An item the menu can offer: it needs a label to be named by.
export const isUsableAction = action => Boolean(action) && isText(action.label)

// a modified click (new tab or window) keeps the link's own behavior
const modified = event =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

/**
 * The click handler of a command or destination. A destination that also declares onSelect is handled in
 * place: its navigation is stopped on the native event only, so an enclosing menu still closes.
 */
export const activate = action => event => {
  if (typeof action.onSelect !== 'function') return
  if (isText(action.href)) {
    if (modified(event)) return
    event.nativeEvent.preventDefault()
  }
  action.onSelect(event)
}

const NOT_BLOCKED = { blocked: false, reason: null }

export const blockedState = (rowActionState, action) => {
  const state =
    typeof rowActionState === 'function' ? rowActionState(action) : null
  return state && state.blocked ? state : NOT_BLOCKED
}

// A blocked control's reason, as its accessible description. The reason node is hidden inside the control,
// so it adds nothing to the control's name, and a menu item stays one element.
export const blockedReason = (state, id) =>
  state.blocked && state.reason && id
    ? {
        describedBy: id,
        node: (
          <span id={id} hidden>
            {state.reason}
          </span>
        ),
      }
    : { describedBy: undefined, node: null }

/**
 * One menu item, rendered as a direct child of Menu so it takes the menu's item role and dismissal. A
 * reasonId lets a blocked item describe why.
 */
export const menuItem = (action, index, state = NOT_BLOCKED, reasonId) => {
  const blocked = Boolean(action.disabled || state.blocked)
  const reason = blockedReason(state, reasonId && `${reasonId}-${index}`)
  return (
    <Button
      key={action.key ?? index}
      href={isText(action.href) ? action.href : undefined}
      className={`me-menu-item${action.danger ? ' is-danger' : ''}`}
      icon={iconClass(action.icon, 'me-menu-ico')}
      aria-disabled={blocked || undefined}
      aria-describedby={reason.describedBy}
      data-tooltip={state.blocked && state.reason ? state.reason : undefined}
      onClick={activate(action)}
    >
      <span>{action.label}</span>
      {isText(action.hint) && (
        <span className="me-menu-hint">{action.hint}</span>
      )}
      {reason.node}
    </Button>
  )
}

export const menuWidth = width =>
  typeof width === 'number' && Number.isFinite(width) && width > 0
    ? width
    : DEFAULT_MENU_MIN_WIDTH
